import { createSelector } from "@reduxjs/toolkit"
import { RootState } from "integrations/redux/rootReducer"
import { memoize } from "nodash"
import { Text } from "@nova/protobuf"
import { LineWidth } from "@nova/constants"
import { getAncestors } from "panels/content/side-panel/utils"
import { selectAllEntitiesMap } from "panels/content/side-panel/slice"

const root = (state: RootState) => state.project.data.texts

export const selectAllTexts = (state: RootState) => root(state)
export const selectNonArchivedTexts = createSelector([selectAllTexts], (texts) =>
    texts.filter((text) => !text.archivedTimestamp)
)
export const selectTextsMap = createSelector([selectAllTexts], (texts) => {
    return texts.reduce(
        (acc, cur) => {
            acc[cur.id] = cur
            return acc
        },
        {} as Record<string, Text>
    )
})
export const selectTextById = memoize((id: string) =>
    createSelector([selectTextsMap], (map) => map[id])
)

export const selectNextTextNumber = createSelector([selectAllTexts], (texts) => texts.length + 1)

export const selectVisibleTexts = createSelector(
    [selectNonArchivedTexts, selectAllEntitiesMap],
    (texts, entityMap) =>
        texts.filter(
            (text) =>
                text.visible &&
                !getAncestors(text.id, entityMap).find((ancestor) => !ancestor.visible)
        )
)

export const selectTextColorById = memoize((id: string) =>
    createSelector([selectTextById(id)], (text) => text?.color)
)

export const selectTextIsBoldById = memoize((id: string) =>
    createSelector([selectTextById(id)], (text) => text?.bold)
)
export const selectTextFontSizeById = memoize((id: string) =>
    createSelector([selectTextById(id)], (text) => text?.fontSize)
)
export const selectTextRotationById = memoize((id: string) =>
    createSelector([selectTextById(id)], (text) => text?.rotation)
)
export const selectTextContentById = memoize((id: string) =>
    createSelector([selectTextById(id)], (text) => text?.text)
)

export const selectTextOutlineColorById = memoize((id: string) =>
    createSelector([selectTextById(id)], (text) => text?.outlineColor)
)
export const selectTextOutlineWidthById = memoize((id: string) =>
    createSelector([selectTextById(id)], (text) => text?.outlineWidth as LineWidth)
)
